/** @file Projects browser thumbnails: renders stale ones one at a time (at most 8 per list load), keeps URLs. */
import { useEffect, useMemo, useRef, useState } from 'react';
import type { ProjectListItem } from '../../api/apiTypes';
import type { ProjectsApi } from '../../api/createProjectsApi';
import { staleThumbnails } from './staleThumbnails';

const PER_LOAD = 8;

/**
 * By project id: a new URL, or null while a queued re-render has not answered (the Worker deletes the old object as
 * the new one lands, so the listed URL can 404 then); missing ids keep their listed thumbnail. A refused render
 * (RATE_LIMITED while an agent renders, offline) ends the queue quietly and its cards fall back to the listed one.
 */
export function useThumbnails(items: readonly ProjectListItem[] | null, client: Pick<ProjectsApi, 'renderThumbnail'>): Partial<Record<string, string | null>> {
  const [urls, setUrls] = useState<Partial<Record<string, string>>>({});
  const [unanswered, setUnanswered] = useState<ReadonlySet<string>>(new Set());
  const asked = useRef(new Set<string>());
  const stale = useMemo(() => (items === null ? [] : staleThumbnails(items, PER_LOAD)), [items]);
  useEffect(() => {
    let stopped = false;
    const queue = stale.filter((id) => !asked.current.has(id));
    const giveUp = (ids: readonly string[]): void => { setUnanswered((prev) => new Set([...prev, ...ids])); };
    const run = async (): Promise<void> => {
      // Sequential on purpose: one heavy render at a time per isolate (R-E7-04).
      for (const [i, id] of queue.entries()) {
        if (stopped) return;
        asked.current.add(id);
        const { thumbUrl } = await client.renderThumbnail(id).catch((thrown: unknown) => { giveUp(queue.slice(i)); throw thrown; });
        if (thumbUrl === null) giveUp([id]);
        else setUrls((prev) => ({ ...prev, [id]: thumbUrl }));
      }
    };
    run().catch(() => undefined);
    return () => { stopped = true; };
  }, [stale, client]);
  return useMemo(() => {
    const waiting = stale.filter((id) => !unanswered.has(id)).map((id): [string, null] => [id, null]);
    return { ...Object.fromEntries(waiting), ...urls };
  }, [stale, unanswered, urls]);
}
